import React from 'react';
import { FolderOpen, Search as SearchIcon } from 'lucide-react';

/**
 * The two empty states a list can be in — and they are genuinely different
 * situations, which is why this takes a `variant` rather than one generic
 * "no data" box:
 *
 *  - `first-run`: nothing exists yet. The user hasn't failed at anything; they
 *    haven't started. Room to explain what the thing IS and offer the action
 *    that creates the first one. Tillgångar does this well ("En tillgång är
 *    något företaget köper och använder i flera år…").
 *
 *  - `no-matches`: rows exist, the current filter/search hides them all. Quiet,
 *    small, echoes the query back and offers a way out. Never show a
 *    create-CTA here — the user is looking, not missing something.
 *
 * Pages pick with `isFiltered` from useListControls.
 */
export const EmptyState = ({
    variant = 'first-run',
    // Pages pass the icon from their own masthead — Package for Tillgångar,
    // Users for Kunder, Landmark for Bankhändelser — so an empty list still
    // says which list it is. The open folder is only the fallback for a page
    // that nominates nothing; every page worth an empty state should pass one.
    icon: Icon = FolderOpen,
    title,
    description,
    // The searched-for text, echoed back so it's obvious what was matched on.
    query,
    // Buttons — first-run only; ignored for no-matches by design.
    actions,
    className = '',
}) => {
    if (variant === 'no-matches') {
        return (
            <div className={`ll-empty ll-empty--quiet ${className}`.trim()}>
                <SearchIcon size={32} className="ll-empty__glyph" aria-hidden="true" />
                <p className="ll-empty__headline">{title || 'Inga träffar'}</p>
                <p className="ll-empty__body">
                    {query
                        ? `Inget matchar "${query}".`
                        : description || 'Byt filter för att se fler.'}
                </p>
            </div>
        );
    }

    return (
        <div className={`ll-empty ${className}`.trim()}>
            {Icon && <Icon size={48} strokeWidth={1.5} className="ll-empty__icon" aria-hidden="true" />}
            {title && <h5 className="ll-empty__title">{title}</h5>}
            {description && <p className="ll-empty__description">{description}</p>}
            {actions && <div className="ll-empty__actions">{actions}</div>}
        </div>
    );
};

export default EmptyState;
